import React, { useState, useEffect, useRef } from 'react';
import { oneKlicks } from "../../../../SDK/oneclick";
import { getuserdata } from "../../../../services/apiService";
import GoogleLocation from "../../../../components/GoogleLocation";

const Defaultlocation = ({selectedCountry, setSelectedCountry, searchAddress, setSearchAddress, mapCenter, setMapCenter, userdata}) => {
    const [selectedLocation, setSelectedLocation] = useState(mapCenter);
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [manualLat, setManualLat] = useState(selectedLocation?.lat || '');
    const [manualLng, setManualLng] = useState(selectedLocation?.lng || '');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const searchRef = useRef(null);

    useEffect(() => {
        const initializeLocation = async () => {
            await fetchCountries();
            const userLocationFound = await loadUserLocation();
            // Only load saved location if user location is not found
            if (!userLocationFound) {
                await loadSavedLocation();
            }
            setIsLoading(false);
        };
        initializeLocation();
    }, []);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Helper to ensure lat/lng are always numbers
    const toNumberLatLng = (obj) => ({
        lat: typeof obj.lat === 'string' ? parseFloat(obj.lat) : obj.lat,
        lng: typeof obj.lng === 'string' ? parseFloat(obj.lng) : obj.lng
    });

    const fetchCountries = async () => {
        try {
            const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,flags');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (!Array.isArray(data)) {
                console.error("Unexpected response format:", data);
                throw new Error("Unexpected response format: countries data is not an array");
            }
            const formattedCountries = data
                .filter(country => country.name && country.name.common && country.cca2 && country.flags && country.flags.png)
                .map(country => ({
                    name: country.name.common,
                    code: country.cca2.toLowerCase(),
                    flag: country.flags.png
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setCountries(formattedCountries);
        } catch (error) {
            console.error('Error fetching countries:', error);
            setCountries([]);
        }
    };    

    const geocodeAddress = async (address) => {
        if (!address || !address.trim()) return null;
        
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&accept-language=en&addressdetails=1`
            );
            const data = await response.json();
            if (data && data.length > 0) {
                const result = data[0];
                return {
                    lat: parseFloat(result.lat),
                    lng: parseFloat(result.lon),
                    address: result.display_name,
                    country: result.address?.country_code?.toLowerCase() || 'india'
                };
            }
        } catch (error) {
            console.error('Error geocoding address:', error);
        }
        return null;
    };

    const loadUserLocation = async () => {
        try {
            const userId = await getuserdata("id");
            const tableSchema = "";
            const tableName = "users";
            const condition = { id: userId };
            const orderBy = "";
            const search = "";
            const pagination = "";
            const margedata = "";
            
            const result = await oneKlicks.read(tableSchema, tableName, condition, orderBy, search, pagination, margedata);
            if (result.success && result.data.length > 0) {
                const user = result.data[0];
                let locationFound = false;
                
                // Priority 1: Check for latitude/longitude fields
                if (user.latitude && user.longitude) {
                    const location = {
                        lat: parseFloat(user.latitude),
                        lng: parseFloat(user.longitude)
                    };
                    setUserLocation(location);
                    setMapCenter(location);
                    setSelectedLocation(location);
                    setManualLat(location.lat.toString());
                    setManualLng(location.lng.toString());
                    locationFound = true;
                    
                    // If address exists, set it
                    if (user.address) {
                        setSearchAddress(user.address);
                        setSearchQuery(user.address);
                    }
                    return true;
                } 
                // Priority 2: Check for location JSON field
                else if (user.location) {
                    try {
                        const locationData = typeof user.location === 'string' 
                            ? JSON.parse(user.location) 
                            : user.location;
                        if (locationData.lat && locationData.lng) {
                            const location = toNumberLatLng({
                                lat: locationData.lat,
                                lng: locationData.lng
                            });
                            setUserLocation(location);
                            setMapCenter(location);
                            setSelectedLocation(location);
                            setManualLat(location.lat.toString());
                            setManualLng(location.lng.toString());
                            locationFound = true;
                            
                            if (locationData.address) {
                                setSearchAddress(locationData.address);
                                setSearchQuery(locationData.address);
                            }
                            if (locationData.country) {
                                setSelectedCountry(locationData.country);
                            }
                            return true;
                        }
                    } catch (e) {
                        console.error('Error parsing location JSON:', e);
                    }
                }
                
                // Priority 3: If no lat/lng found, geocode the address from users table
                if (!locationFound && user.address) {
                    setSearchAddress(user.address);
                    setSearchQuery(user.address);
                    
                    const geocoded = await geocodeAddress(user.address);
                    if (geocoded) {
                        const location = toNumberLatLng({
                            lat: geocoded.lat,
                            lng: geocoded.lng
                        });
                        setUserLocation(location);
                        setMapCenter(location);
                        setSelectedLocation(location);
                        setManualLat(location.lat.toString());
                        setManualLng(location.lng.toString());
                        
                        if (geocoded.address) {
                            setSearchAddress(geocoded.address);
                            setSearchQuery(geocoded.address);
                        }
                        if (geocoded.country) {
                            setSelectedCountry(geocoded.country);
                        }
                        return true;
                    }
                }
            }
            return false;
        } catch (error) {
            console.error('Error loading user location:', error);
            return false;
        }
    };

    const loadSavedLocation = async () => {
        try {
            const userId = await getuserdata("id");
            const tableSchema = "";
            const tableName = "insurance_default_location";
            const condition = {user_id: userId };
            const orderBy = "";
            const search = "";
            const pagination = "";
            const margedata = "";
            
            const result = await oneKlicks.read(tableSchema, tableName, condition, orderBy, search, pagination, margedata);
            if (result.success && result.data.length > 0) {
                const location = result.data[0];
                setSelectedCountry(location.country || 'india');
                setSearchAddress(location.address || '');
                const center = toNumberLatLng({
                    lat: location.lat || 20.5937,
                    lng: location.lng || 78.9629
                });
                setMapCenter(center);
                setSelectedLocation(center);
                setManualLat(center.lat.toString());
                setManualLng(center.lng.toString());
            }
        } catch (error) {
            console.error('Error loading saved location:', error);
        }
    };

    const handleCountryChange = async (e) => {
        const country = countries.find(c => c.code === e.target.value);
        if (country) {
            setSelectedCountry(country.code);
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(country.name)}&limit=1&accept-language=en`
                );
                const data = await response.json();
                if (data && data.length > 0) {
                    const lat = parseFloat(data[0].lat);
                    const lng = parseFloat(data[0].lon);
                    const center = toNumberLatLng({ lat, lng });
                    setMapCenter(center);
                    setSelectedLocation(center);
                    setSearchAddress(data[0].display_name);
                    setManualLat(lat.toString());
                    setManualLng(lng.toString());
                }
            } catch (error) {
                console.error('Error geocoding country:', error);
            }
        }
    };

    const handleAddressChange = (e) => {
        const newAddress = e.target.value;
        setSearchAddress(newAddress);
        setSearchQuery(newAddress);
    };

    const searchLocation = async (query) => {
        if (!query.trim()) return;
        
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en&addressdetails=1`
            );
            const data = await response.json();
            setSearchResults(data);
            setShowSearchResults(true);
        } catch (error) {
            console.error('Error searching location:', error);
        }
    };

    const selectSearchResult = (result) => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        const newLocation = toNumberLatLng({ lat, lng });
        setSelectedLocation(newLocation);
        setMapCenter(newLocation);
        setSearchAddress(result.display_name);
        setManualLat(lat.toString());
        setManualLng(lng.toString());
        setShowSearchResults(false);
        setSearchQuery(result.display_name);

        // Extract country from address details
        if (result.address) {
            const countryCode = result.address.country_code?.toLowerCase();
            if (countryCode) {
                setSelectedCountry(countryCode);
            }
        }
    };

    const handleManualLatLngChange = (type, value) => {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        if (type === 'lat') {
            setManualLat(value);
        } else {
            setManualLng(value);
        }

        if (type === 'lat' && manualLng && !isNaN(parseFloat(manualLng))) {
            updateLocationFromLatLng(numValue, parseFloat(manualLng));
        } else if (type === 'lng' && manualLat && !isNaN(parseFloat(manualLat))) {
            updateLocationFromLatLng(parseFloat(manualLat), numValue);
        }
    };

    const updateLocationFromLatLng = async (lat, lng) => {
        const newLocation = toNumberLatLng({ lat, lng });
        setSelectedLocation(newLocation);
        setMapCenter(newLocation);
        
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en&addressdetails=1`
            );
            const data = await response.json();
            if (data && data.display_name) {
                setSearchAddress(data.display_name);
                setSearchQuery(data.display_name);
                
                if (data.address && data.address.country_code) {
                    const countryCode = data.address.country_code.toLowerCase();
                    setSelectedCountry(countryCode);
                }
            }
        } catch (error) {
            console.error('Error reverse geocoding:', error);
        }
    };

    const handleLocationSelect = (location) => {
        const loc = toNumberLatLng({ lat: location.lat, lng: location.lng });
        setSelectedLocation(loc);
        setMapCenter(loc);
        setManualLat(loc.lat.toString());
        setManualLng(loc.lng.toString());
        updateLocationFromLatLng(loc.lat, loc.lng);
    };

    if (isLoading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>Loading map...</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                    Please wait while we initialize the map
                </div>
            </div>
        );
    }

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-skills dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Default Location</h3>
                    </div>
                    <div className="dc-skillscontent-holder">
                        <div className="dc-formtheme dc-skillsform">
                            <fieldset>
                                <div style={{ width: '100%', height: '400px', position: 'relative' }}>
                                    <GoogleLocation 
                                        initialLocation={userLocation || mapCenter}
                                        onLocationSelect={handleLocationSelect}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label htmlFor="country-select">Country</label>
                                        <span className="dc-select">
                                            <select
                                                id="country-select"
                                                value={selectedCountry}
                                                onChange={handleCountryChange}
                                                className="form-control"
                                                style={{ height: "48px", fontSize: "18px" }}
                                            >
                                                <option value="">Select Country</option>
                                                {countries.map(country => (
                                                    <option
                                                        key={country.code}
                                                        value={country.code}
                                                        data-flag={country.flag}
                                                    >
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </span>
                                    </div>
                                    <div ref={searchRef} style={{ flex: 2, position: 'relative' }}>
                                        <label htmlFor="address-input">Search Address</label>
                                        <input
                                            type="text"
                                            id="address-input"
                                            className="form-control"
                                            placeholder="Search for address"
                                            value={searchQuery || searchAddress || userdata?.address || ''}
                                            onChange={handleAddressChange}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    searchLocation(searchQuery);
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            style={{ 
                                                position: 'absolute', 
                                                right: '5px', 
                                                top: '50%', 
                                                transform: 'translateY(-50%)',
                                                padding: '5px 10px',
                                                fontSize: '12px'
                                            }}
                                            onClick={() => searchLocation(searchQuery)}
                                        >
                                            Search
                                        </button>
                                        
                                        {showSearchResults && searchResults.length > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                background: 'white',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                zIndex: 1000,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                                {searchResults.map((result, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid #eee'
                                                        }}
                                                        onClick={() => selectSearchResult(result)}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                    >
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                            {result.display_name}
                                                        </div>
                                                        {result.address && (
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                {result.address.city || result.address.town || result.address.village} 
                                                                {result.address.state && `, ${result.address.state}`}
                                                                {result.address.country && `, ${result.address.country}`}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label htmlFor="latitude-input">Latitude</label>
                                        <input
                                            type="text"
                                            id="latitude-input"
                                            className="form-control"
                                            value={manualLat || userdata?.latitude || ''}
                                            onChange={(e) => handleManualLatLngChange('lat', e.target.value)}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label htmlFor="longitude-input">Longitude</label>
                                        <input
                                            type="text"
                                            id="longitude-input"
                                            className="form-control"
                                            value={manualLng || userdata?.longitude || ''}
                                            onChange={(e) => handleManualLatLngChange('lng', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Defaultlocation;

